/**
 * GraphQL hooks for downloads
 *
 * Provides query, mutation, and subscription hooks for the download system.
 * Replaces the REST client (web/src/api/downloads.ts).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { gql, useMutation, useQuery, useSubscription } from 'urql';
import createDebug from 'debug';

const debug = createDebug('app:downloads');

// Types

export interface DownloadFile {
	filename: string;
	sizeBytes: number;
	mimeType: string;
	url: string;
}

export interface Download {
	id: string;
	linkId: number;
	status: 'PENDING' | 'DOWNLOADING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
	tool: string | null;
	files: DownloadFile[];
	totalSizeBytes: number;
	error: string | null;
	retryCount: number;
	startedAt: string | null;
	completedAt: string | null;
	createdAt: string;
}

export interface DownloadStats {
	pending: number;
	downloading: number;
	completed: number;
	failed: number;
	cancelled: number;
}

// GraphQL documents

const DownloadQuery = gql`
	query GetDownload($linkId: Int!) {
		download(linkId: $linkId) {
			id
			linkId
			status
			tool
			files {
				filename
				sizeBytes
				mimeType
				url
			}
			totalSizeBytes
			error
			retryCount
			startedAt
			completedAt
			createdAt
		}
	}
`;

const TriggerDownloadMutation = gql`
	mutation TriggerDownload($linkId: Int!, $tool: DownloadTool) {
		triggerDownload(linkId: $linkId, tool: $tool) {
			download {
				id
				linkId
				status
				tool
				files {
					filename
					sizeBytes
					mimeType
					url
				}
				totalSizeBytes
				error
				retryCount
				createdAt
			}
			suggestedTool
			errors {
				field
				message
			}
		}
	}
`;

const CancelDownloadMutation = gql`
	mutation CancelDownload($linkId: Int!) {
		cancelDownload(linkId: $linkId) {
			success
			errors {
				field
				message
			}
		}
	}
`;

const RetryDownloadMutation = gql`
	mutation RetryDownload($linkId: Int!) {
		retryDownload(linkId: $linkId) {
			download {
				id
				linkId
				status
				tool
				error
				retryCount
				createdAt
			}
			errors {
				field
				message
			}
		}
	}
`;

const DownloadChangesSubscription = gql`
	subscription DownloadChanges($linkId: Int!) {
		downloadChanges(linkId: $linkId) {
			downloadId
			linkId
			status
			download {
				id
				linkId
				status
				tool
				files {
					filename
					sizeBytes
					mimeType
					url
				}
				totalSizeBytes
				error
				retryCount
				startedAt
				completedAt
				createdAt
			}
			timestamp
		}
	}
`;

// Hooks

/**
 * Query download status for a link + subscribe to real-time updates
 */
export function useDownloadStatus(linkId: number | null) {
	const [download, setDownload] = useState<Download | null | undefined>(undefined);

	// Initial query
	const [queryResult] = useQuery({
		query: DownloadQuery,
		variables: { linkId: linkId! },
		pause: linkId === null,
		requestPolicy: 'cache-and-network',
	});

	// Update state from query
	useEffect(() => {
		if (queryResult.fetching) {
			setDownload(undefined);
			return;
		}
		setDownload(queryResult.data?.download ?? null);
	}, [queryResult.data, queryResult.fetching]);

	// Subscription for real-time updates
	const prevEventRef = useRef<string | null>(null);
	const [subResult] = useSubscription({
		query: DownloadChangesSubscription,
		variables: { linkId: linkId! },
		pause: linkId === null,
	});

	// Process subscription events
	useEffect(() => {
		if (!subResult.data?.downloadChanges) return;

		const event = subResult.data.downloadChanges;
		const eventKey = `${event.status}:${event.timestamp}`;
		if (prevEventRef.current === eventKey) return;
		prevEventRef.current = eventKey;

		debug('subscription event: %s for link %d', event.status, event.linkId);

		if (event.download) {
			setDownload(event.download);
		} else {
			// Status-only update (e.g. cancelled)
			setDownload((prev) => prev ? { ...prev, status: event.status } : prev);
		}
	}, [subResult.data]);

	// Reset when linkId changes
	useEffect(() => {
		setDownload(undefined);
		prevEventRef.current = null;
	}, [linkId]);

	return {
		download,
		loading: download === undefined,
		error: queryResult.error ?? null,
	};
}

/**
 * Trigger a download mutation
 */
export function useTriggerDownload() {
	const [result, execute] = useMutation(TriggerDownloadMutation);

	const trigger = useCallback(async (linkId: number, tool?: string) => {
		const toolEnum = tool === 'yt-dlp' ? 'YT_DLP' : tool === 'gallery-dl' ? 'GALLERY_DL' : undefined;
		const res = await execute({ linkId, tool: toolEnum });

		if (res.error) throw res.error;
		if (res.data?.triggerDownload.errors?.length) {
			throw new Error(res.data.triggerDownload.errors[0].message);
		}

		return res.data?.triggerDownload;
	}, [execute]);

	return { trigger, isPending: result.fetching };
}

/**
 * Cancel a download mutation
 */
export function useCancelDownload() {
	const [result, execute] = useMutation(CancelDownloadMutation);

	const cancel = useCallback(async (linkId: number) => {
		const res = await execute({ linkId });
		if (res.error) throw res.error;
		if (res.data?.cancelDownload.errors?.length) {
			throw new Error(res.data.cancelDownload.errors[0].message);
		}
		return res.data?.cancelDownload;
	}, [execute]);

	return { cancel, isPending: result.fetching };
}

/**
 * Retry a failed download mutation
 */
export function useRetryDownload() {
	const [result, execute] = useMutation(RetryDownloadMutation);

	const retry = useCallback(async (linkId: number) => {
		const res = await execute({ linkId });
		if (res.error) throw res.error;
		if (res.data?.retryDownload.errors?.length) {
			throw new Error(res.data.retryDownload.errors[0].message);
		}
		return res.data?.retryDownload;
	}, [execute]);

	return { retry, isPending: result.fetching };
}

// Helpers

export function formatBytes(bytes: number): string {
	if (bytes === 0) return '0 B';
	const units = ['B', 'KB', 'MB', 'GB'];
	const i = Math.floor(Math.log(bytes) / Math.log(1024));
	const val = bytes / 1024 ** i;
	return `${val.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
